<template>
  <div :class="{ row: isAtRoot }">
    <div class="flex flex-row items-center one-of-container">
      <span v-if="schemaType === 'oneOf'" class="one-of-title text-muted">{{
        $t('documentation.api.schema.one-of')
      }}</span>
      <span v-if="schemaType === 'anyOf'" class="one-of-title text-muted">{{
        $t('documentation.api.schema.any-of')
      }}</span>
      <v-tabs v-if="schemaArray.every(tab => tab.title)" class="tabs tab-bar-width">
        <v-tab
          v-for="(tab, index) in schemaArray"
          :key="tab.title + index"
          :label="tab.title"
          dense
          @click="activated(index)"
        />
        <v-tab-panel v-for="tab in schemaArray" :key="tab.title" />
      </v-tabs>
      <v-select
        v-else
        :aria-label="$t('documentation.api.schema.select-one-of-option')"
        dense
        class="grow select"
        @change="selected($event.target._selectedIndex)"
      >
        <v-option
          v-for="(item, index) in schemaArray"
          :key="item.description"
          :value="`${index}`"
          :text="item.description ? convertToPlainText(item.description) : `Option ${index + 1}`"
        />
      </v-select>
    </div>
  </div>
  <api-schema-object
    v-if="schemaArray[position]"
    :key="position"
    :schema="schemaArray[position]"
    :type="type"
    :example-generator-args="narrowedExampleGeneratorArgs"
  />
</template>
<script setup lang="ts">
import { computed, inject, PropType, ref } from 'vue';
import { VOption, VSelect, VTab, VTabPanel, VTabs } from '@vonage/vivid-vue';
import { ExampleGeneratorArgs, SchemaAnyOf, SchemaOneOf } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import { OpenAPIV3 } from 'openapi-types';
import ApiSchemaObject from '@/components/api/content/operations/schema/ApiSchemaObject.vue';

const props = defineProps({
  schemaType: { type: String as PropType<'anyOf' | 'oneOf'>, required: false },
  schemaArray: { type: Array as PropType<SchemaOneOf[] | SchemaAnyOf[]>, required: true },
  type: { type: String as PropType<'request' | 'payload' | 'response'>, required: false },
  exampleGeneratorArgs: { type: Object as PropType<ExampleGeneratorArgs>, required: false },
});

/// Are we still at root, i.e. not in a row
const isAtRoot = computed(() => !!props.type);
const position = ref(0);
const setArrayItemPosition = inject<((index: number) => void) | null>('setArrayItemPosition', null);

const activated = (activeIndex: number) => {
  position.value = activeIndex;
  setArrayItemPosition?.(activeIndex);
};

const selected = (value: number) => {
  position.value = value;
  setArrayItemPosition?.(value);
};

const narrowedExampleGeneratorArgs = computed(() => {
  if (props.exampleGeneratorArgs?.schema.oneOf) {
    // Narrow it down to the selected schema
    return {
      ...props.exampleGeneratorArgs,
      schema: props.exampleGeneratorArgs.schema.oneOf[position.value] as OpenAPIV3.SchemaObject,
      // When narrowing a oneOf, use schema description as example title
      title: (props.exampleGeneratorArgs.schema.oneOf[position.value] as OpenAPIV3.SchemaObject).description,
    };
  }
  if (props.exampleGeneratorArgs?.schema.anyOf) {
    // Narrow it down to the selected schema
    return {
      ...props.exampleGeneratorArgs,
      schema: props.exampleGeneratorArgs.schema.anyOf[position.value] as OpenAPIV3.SchemaObject,
      // When narrowing a oneOf, use schema description as example title
      title: (props.exampleGeneratorArgs.schema.anyOf[position.value] as OpenAPIV3.SchemaObject).description,
    };
  }

  if (props.exampleGeneratorArgs?.schema.type === 'array') {
    const schemaItems = props.exampleGeneratorArgs.schema.items as OpenAPIV3.SchemaObject | undefined;
    const variants = (schemaItems?.anyOf || schemaItems?.oneOf) as OpenAPIV3.SchemaObject[] | undefined;
    if (variants?.length) {
      const selectedItem = variants[position.value] as OpenAPIV3.SchemaObject;
      return {
        ...props.exampleGeneratorArgs,
        schema: { ...props.exampleGeneratorArgs.schema, items: selectedItem } as OpenAPIV3.SchemaObject,
        title: selectedItem.description,
      };
    }
  }
  return props.exampleGeneratorArgs;
});

const convertToPlainText = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent;
};
</script>

<style scoped lang="scss">
.one-of-container {
  gap: 8px;
}

.one-of-title {
  text-transform: uppercase;
  font-size: 8px;
  line-height: 10px;
  letter-spacing: 1px;
  font-weight: 600;
}

.select {
  padding: 8px 0;
}
.tab-bar-width {
  width: 100%;
}
</style>
